import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Tab, Tabs, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import SearchIcon from '@mui/icons-material/Search';
import AddTaskIcon from '@mui/icons-material/AddTask';
import BaseModal from '../templates/BaseModal';
import CameraFeed from './CameraFeed';
import TaskFooter from './TaskFooter';

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  mainContainer: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  header: {
    bgcolor: 'background.paper', px: 2.5, pt: 2.5, pb: 2, flexShrink: 0,
    borderBottom: '1px solid', borderColor: 'divider',
  },
  headerContent: { display: 'flex', alignItems: 'flex-start', gap: 1.5 },
  headerIcon: {
    width: 44, height: 44, borderRadius: 2.5, bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25,
  },
  headerIconImg: { fontSize: 22, color: 'text.secondary' },
  headerText: { flexGrow: 1, minWidth: 0 },
  headerTitle: { lineHeight: 1.35 },
  headerMeta: { mt: 0.3 },
  closeButton: { ml: 0.5, flexShrink: 0 },
  tabs: {
    flexShrink: 0,
    bgcolor: 'background.paper',
    minHeight: 48,
    '& .MuiTab-root': {
      minHeight: 48,
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      flex: 1,
      color: 'text.disabled',
      '&:hover': { color: 'text.secondary', bgcolor: 'action.hover' },
    },
    '& .Mui-selected': { fontWeight: 700, color: 'text.primary !important' },
    '& .MuiTabs-indicator': { backgroundColor: 'primary.main', height: 2 },
  },
};

// ── ScannerModal ──────────────────────────────────────────────────────────────

export default function ScannerModal({
  open,
  onClose,
  searchProduct,
  resetSearch,
  loadingSearch,
  searchResults,
  resourcesReportOptions,
  setReportParameters,
  createTasks,
  loadingReport,
  aislesList,
  getAisles,
  getTasksProgress,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [tab, setTab] = useState(0);
  const [taskType, setTaskType] = useState('');
  const [aisle, setAisle] = useState('');
  const [unfreezeCamera, setUnfreezeCamera] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  // True once the scanner itself has triggered a search in the current session.
  // Gates:
  //   1. Auto-select from `searchResults` (avoid pre-selecting a product from a
  //      pre-existing ProductsView search).
  //   2. Whether Generate-task's TaskFooter sees `searchResults` at all —
  //      otherwise a pre-existing Products search leaks stale candidates and
  //      the user could create a task for the wrong item.
  const [hasScanned, setHasScanned] = useState(false);
  const hasScannedRef = useRef(false);
  const generatingRef = useRef(false);
  const markScanned = useCallback(() => {
    hasScannedRef.current = true;
    setHasScanned(true);
  }, []);
  const clearScanned = useCallback(() => {
    hasScannedRef.current = false;
    setHasScanned(false);
  }, []);

  useEffect(() => {
    if (!hasScanned) return;
    if (searchResults?.length === 1) {
      const p = searchResults[0];
      setSelectedProduct(p);
      setReportParameters('item', p.item);
      setReportParameters('ean', p.ean);
    } else {
      setSelectedProduct(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // setReportParameters is a stable Redux dispatch — safe to omit
  }, [hasScanned, searchResults]);

  useEffect(() => {
    if (!open) return;
    setSelectedProduct(null);
    setTab(0);
    clearScanned();
    resetSearch();
  }, [open, clearScanned, resetSearch]);

  useEffect(() => {
    if (tab === 1 && open) {
      getAisles();
      if (!resourcesReportOptions || resourcesReportOptions.length === 0) getTasksProgress();
    }
  }, [tab, open, getAisles, getTasksProgress, resourcesReportOptions]);

  const handleClose = () => {
    setTaskType('');
    setAisle('');
    setTab(0);
    setSelectedProduct(null);
    setUnfreezeCamera(v => v + 1);
    // Only reset search if the scanner itself wrote to it; otherwise the previous
    // ProductsView search should survive closing the modal with X.
    const wipedSearch = hasScannedRef.current;
    if (wipedSearch) resetSearch();
    clearScanned();
    onClose({ wipedSearch });
  };

  const handleResetScan = () => {
    setSelectedProduct(null);
    setUnfreezeCamera(v => v + 1);
    // Only clear if the scanner wrote results; otherwise preserve ProductsView's search.
    if (hasScannedRef.current) {
      resetSearch();
      clearScanned();
    }
  };

  const handleTabChange = (_, newTab) => {
    if (isSearching) return;
    setTab(newTab);
    setSelectedProduct(null);
    setUnfreezeCamera(v => v + 1);
    // Clear only scanner-written results; preserve any pre-existing ProductsView search.
    if (newTab === 0 && hasScannedRef.current) {
      resetSearch();
      clearScanned();
    }
  };

  // ── Search mode ──────────────────────────────────────────────────────────────

  const handleCameraSearch = useCallback(async (code) => {
    if (!code) return false;
    markScanned();
    const results = await searchProduct('barcode', code.trim());
    // Close without resetSearch — ProductsView needs the results already in the store
    setTaskType('');
    setAisle('');
    setTab(0);
    setSelectedProduct(null);
    setUnfreezeCamera(v => v + 1);
    clearScanned();
    onClose({ keepResults: true });
    navigate(results?.length === 1
      ? `/products?barcode=${encodeURIComponent(code.trim())}`
      : `/products?search=${encodeURIComponent(code.trim())}`
    );
    return false; // CameraFeed should not continue — modal is closing
  }, [searchProduct, navigate, onClose, clearScanned]);

  // ── Create task mode ─────────────────────────────────────────────────────────

  const handleCameraTask = useCallback(async (code) => {
    // Only clear between scans AFTER the scanner itself has written results;
    // on the first scan of a session, preserve ProductsView's previous search
    // — searchProduct will overwrite it on success.
    if (hasScannedRef.current) resetSearch();
    markScanned();
    const results = await searchProduct('barcode', code.trim());
    const found = results?.length > 0;
    // If not found, tell CameraFeed to resume the scan loop
    return !found;
  }, [searchProduct, resetSearch, markScanned]);

  const handleSelectProduct = (product) => {
    if (!product) { setSelectedProduct(null); return; }
    setSelectedProduct(product);
    setReportParameters('item', product.item);
    setReportParameters('ean', product.ean);
  };

  const handleCreateTask = async () => {
    if (!taskType || !selectedProduct || generatingRef.current) return;
    generatingRef.current = true;
    try {
      setReportParameters('task_type', taskType);
      setReportParameters('aisle', aisle);
      const result = await createTasks();
      if (result?.success) {
        getTasksProgress();
        handleClose();
      }
    } finally {
      generatingRef.current = false;
    }
  };

  return (
    <BaseModal open={open} onClose={handleClose} dialogWidth={640} dialogHeight="80vh">
      <Box sx={styles.mainContainer}>

        {/* ── Header ── */}
        <Box sx={styles.header}>
          <Box sx={styles.headerContent}>
            <Box sx={styles.headerIcon}>
              <QrCodeScannerIcon sx={styles.headerIconImg} />
            </Box>
            <Box sx={styles.headerText}>
              <Typography variant="subtitle1" fontWeight={700} noWrap sx={styles.headerTitle}>
                {t('bruno.scanner.title', 'Scan barcode')}
              </Typography>
              <Box sx={styles.headerMeta}>
                <Typography variant="caption" color="text.secondary">
                  {tab === 0
                    ? t('bruno.camera.search', 'Search product')
                    : t('bruno.camera.scan', 'Generate task')}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={styles.closeButton}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* ── Tabs ── */}
        <Tabs value={tab} onChange={handleTabChange} variant="fullWidth" sx={styles.tabs}>
          <Tab icon={<SearchIcon sx={{ fontSize: 15 }} />} iconPosition="start" label={t('bruno.camera.search', 'Search product')} disabled={isSearching} />
          <Tab icon={<AddTaskIcon sx={{ fontSize: 15 }} />} iconPosition="start" label={t('bruno.camera.scan', 'Generate task')} disabled={isSearching} />
        </Tabs>

        {/* ── Camera — shared, fills remaining space ── */}
        <CameraFeed
          onBarcodeDetected={tab === 0 ? handleCameraSearch : handleCameraTask}
          active={open}
          unfreeze={unfreezeCamera}
          onSearchingChange={setIsSearching}
        />

        {/* ── TAB 1: Task footer ── */}
        {tab === 1 && (
          <TaskFooter
            // Gate by hasScanned: if the user landed here with a pre-existing
            // ProductsView search, those results would leak as selectable
            // candidates and allow creating a task for the wrong item.
            searchResults={hasScanned ? searchResults : []}
            selectedProduct={selectedProduct}
            taskType={taskType}
            aisle={aisle}
            resourcesReportOptions={resourcesReportOptions}
            aislesList={aislesList}
            loadingReport={loadingReport}
            onSelectProduct={handleSelectProduct}
            onTaskTypeChange={setTaskType}
            onAisleChange={setAisle}
            onReset={handleResetScan}
            onCreateTask={handleCreateTask}
          />
        )}

      </Box>
    </BaseModal>
  );
}
